import { Controller } from "@hotwired/stimulus";

/** 
 * Controller for accessing shared context data from a script element (type="application/json").
 * The controller should be accessed via the outlets API in case it changes due to rerendering.
 * It is recommended to reference the context controller by id since there could be multiple 
 * context controllers on the same page for different purposes. Plus, the id can double as a way 
 * to differentiate requests on the server side if needed.

 * The script element should be placed in the DOM prior to any elements that depend on it. 
 * That way, the dependent controllers can access the context data during their connect() phase;
 * otherwise they would have to rely on the outletConnected() callback, which complicates their lifecycle management.
 * 
 * Why not just use a utility function to find the script by id, then cache it statically?
 * That would only work if the context data is static for the entire page lifecycle,
 * but we often swap content in and out via AJAX, which may include updated context data.
 * */
export class ContextController<C> extends Controller<HTMLScriptElement> {

    private ctx?: C;

    connect(): void {
        this.ctx = JSON.parse(this.element.textContent);
    }

    disconnect(): void {
        this.ctx = undefined;
    }

    get contextId(): string {
        return this.element.id;
    }

    get contextData(): C {
        if (!this.ctx) {
            throw new Error('ContextController: context accessed before initialization');
        }
        return this.ctx;
    }
}